<script lang="ts" setup>
import type { TypedSchema } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { useUser } from '~/stores/user'
import { useCountriesStore } from '~/stores/countries'
import { storeToRefs } from 'pinia'
import type {
  ConfiguredOrderProductRequest,
  ConfiguredOrderRequest,
  OrderProductRequest,
  OrderRequest,
} from '~/types/api'
import * as zod from 'zod'
import { createOrdersRepository } from '~/utils/repositories/ordersRepository'

const name = 'signup-form'
const countriesStore = useCountriesStore()
await countriesStore.loadCountries()
await countriesStore.loadRegions()

const { countries, countriesLoading, regions, regionsLoading } =
  storeToRefs(countriesStore)

const { t } = useI18n()

const props = defineProps<{
  configurationSlug?: string
  productId?: number
  quantity?: number
  dense?: boolean
  hideSubmit?: boolean
  submitTrigger?: number
  productTitle: string
}>()

const emit = defineEmits(['valid', 'submit', 'resetForm', 'close'])

watch(
  () => props.submitTrigger,
  () => {
    onSubmit()
  }
)

const { requiredString, optionalString, requiredNumericString } = useRules()
const validationSchema: ComputedRef<TypedSchema> = computed(() => {
  return toTypedSchema(
    zod.object({
      fullName: requiredString().refine(
        (val) => {
          const names = ('' + val).split(' ')
          const firstName = names[0]
          const lastName = names[1]
          return (
            requiredString(3).safeParse(firstName).success &&
            requiredString(3).safeParse(lastName).success
          )
        },
        {
          message: t('signup.fullNameError'),
        }
      ),
      email: requiredString().email({
        message: t('base.form.emailNotValid'),
      }),
      phone: optionalString(),
      company: optionalString(),
      title: optionalString(),
      message: optionalString(),
      country: requiredString(),
      region: optionalString(),
      postCode: optionalString(),
    })
  )
})

const { validate, values, errors, setFieldError, meta, validateField } =
  useForm({
    validationSchema,
  })

const { value: fullName } = useField<string>('fullName')
const { value: email } = useField<string>('email')
const { value: phone } = useField<string>('phone')
const { value: company } = useField<string>('company')
const { value: title } = useField<string>('title')
const { value: country } = useField<string>('country')
const { value: region } = useField<string>('region')
const { value: postCode } = useField<string>('postCode')
const { value: message } = useField<string>('message')

const { user } = storeToRefs(useUser())

const { sendEnquireSentEvent, sendEnquireEvent } = useEvent()

onMounted(() => {
  if (user.value) {
    email.value = user.value.email
    fullName.value = `${user.value.firstName} ${user.value.lastName}`
    phone.value = user.value.phone || ''
    company.value = user.value.company || ''
    title.value = user.value.position || ''
  }

  sendEnquireEvent(props.productTitle, !!props.configurationSlug)
})

const isCountryDividedToRegions: ComputedRef<boolean> = computed(() => {
  const countriesWithRegions = Object.keys(regions.value)
  return countriesWithRegions.includes(country.value as string)
})

const isCountryDividedToPostCodes: ComputedRef<boolean> = computed(() => {
  return !!countries.value
    .filter((countryDetail) =>
      countriesStore.countriesWithPostCodeNids.includes(
        countryDetail.value as number
      )
    )
    .find((countryDetail) => countryDetail.value.toString() === country.value)
})

const valid: ComputedRef<boolean> = computed(() => {
  let passed = true
  if (!meta.value.valid) {
    passed = false
  }
  if (
    isCountryDividedToRegions.value &&
    !validateOptionalString('region', region.value)
  ) {
    passed = false
  }
  if (
    isCountryDividedToPostCodes.value &&
    !validateNumericString(
      'postCode',
      postCode.value,
      t('signup.postCodeError')
    )
  ) {
    passed = false
  }
  return passed
})

watch(valid, (val) => {
  emit('valid', val)
})

const toast = useToast()

const { getCaptchaToken } = useVueRecaptcha()
const route = useRoute()
const locale = useI18n

function validateOptionalString(fieldName: string, value: string) {
  const result = requiredString().safeParse(value)
  if (!result.success) {
    setFieldError(fieldName, result.error.formErrors.formErrors[0])
    return false
  }
  return true
}

function validateNumericString(
  fieldName: string,
  value: string,
  message: string
) {
  const result = requiredNumericString(5, message).safeParse(value)
  if (!result.success) {
    setFieldError(fieldName, result.error.formErrors.formErrors[0])
    return false
  }
  return true
}

async function onSubmit() {
  // validates form except region
  const valid = await validate()
  if (!valid.valid) {
    return
  }
  // validate region, reactive schema cannot be used
  // schema needs value from form and useField can be used first after useForm which needs schema
  if (
    isCountryDividedToRegions.value &&
    !validateOptionalString('region', region.value)
  ) {
    return
  }
  if (
    isCountryDividedToPostCodes.value &&
    !validateNumericString(
      'postCode',
      postCode.value,
      t('signup.postCodeError')
    )
  ) {
    return
  }
  const formData = JSON.parse(JSON.stringify(values))
  if (!isCountryDividedToRegions.value) {
    delete formData.region
  }

  const recaptchaToken = await getCaptchaToken('configurationOrderForm')
  if (props.configurationSlug) {
    submitConfigurationForm(recaptchaToken)
  } else {
    submitForm(recaptchaToken)
  }
}

async function submitConfigurationForm(reCaptchaToken: string) {
  const orderData = {
    name: fullName.value,
    email: email.value,
    phone: phone.value,
    company: company.value,
    position: title.value,
    originUrl: route.path,
    countryId: country.value,
    regionId: isCountryDividedToRegions.value ? region.value : undefined,
    postCode: isCountryDividedToPostCodes.value ? postCode.value : undefined,
    language: convertNuxtLocale(locale.value as NuxtLocale),
    recaptchaToken: reCaptchaToken,
    orderProducts: [
      {
        userConfigurationSlug: props.configurationSlug,
        note: message.value,
      } as ConfiguredOrderProductRequest,
    ] as ConfiguredOrderRequest[],
  } as OrderRequest
  try {
    await createOrdersRepository().createConfigured(orderData)
    sendEnquireSentEvent(props.productTitle, true)
    sentModal.value = true
  } catch (err) {
    console.error(err)
    toast.add({
      title: t('base.order.error'),
      description: t('base.order.errorInfo'),
      color: 'red',
    })
  }
}

async function submitForm(recaptchaToken: string) {
  const orderData = {
    name: fullName.value,
    email: email.value,
    phone: phone.value,
    company: company.value,
    position: title.value,
    originUrl: route.path,
    countryId: country.value,
    regionId: isCountryDividedToRegions.value ? region.value : undefined,
    postCode: isCountryDividedToPostCodes.value ? postCode.value : undefined,
    language: convertNuxtLocale(locale.value as NuxtLocale),
    recaptchaToken: recaptchaToken,
    orderProducts: [
      {
        productId: props.productId,
        quantity: props.quantity,
        note: message.value,
      } as OrderProductRequest,
    ] as OrderProductRequest[],
  } as OrderRequest
  try {
    await createOrdersRepository().create(orderData)
    sendEnquireSentEvent(props.productTitle, false)
    sentModal.value = true
  } catch (err) {
    console.error(err)
    toast.add({
      title: t('base.order.error'),
      description: t('base.order.errorInfo'),
      color: 'red',
    })
  }
}
const sentModal = ref(false)
function closeOrder() {
  sentModal.value = false
  emit('close')
}
</script>

<template>
  <UiModal v-model="sentModal">
    <div class="h-full w-full">
      <div class="font-pragmatica-ext pb-5 text-xl font-bold lg:text-2xl">
        {{ $t('base.order.sent') }}
      </div>
      <div class="text-lg">
        {{ $t('base.order.sentInfo') }}
      </div>
      <div>
        <div class="ml-auto w-fit pt-10">
          <UiButtonBlack class="!w-fit capitalize" @click="closeOrder">
            {{ $t('base.close') }}
          </UiButtonBlack>
        </div>
      </div>
    </div>
  </UiModal>
  <form
    class="bg-transparent bg-cover bg-no-repeat pt-6 text-black"
    @submit.prevent="onSubmit"
  >
    <div :class="dense ? 'grid grid-cols-1 md:grid-cols-2' : 'flex flex-col'">
      <FormFieldInput
        v-model="fullName"
        name="fullName"
        :parent-name="name"
        property-name="full-name"
        label="signup.fullName"
        :icon="'user'"
        required
        :error="errors.fullName"
        @blur="validateField('fullName')"
      />
      <FormFieldInput
        v-model="email"
        name="email"
        :parent-name="name"
        property-name="email"
        label="signup.email"
        :icon="'envelope'"
        required
        :error="errors.email"
        autocomplete="username"
        @blur="validateField('email')"
      />
      <FormFieldInput
        v-model="phone"
        name="phone"
        :parent-name="name"
        property-name="phone"
        label="signup.phone"
        :icon="'mobile-screen'"
        :error="errors.phone"
        @blur="validateField('phone')"
      />
      <FormFieldInput
        v-model="company"
        name="company"
        :parent-name="name"
        property-name="company"
        label="signup.company"
        :icon="'building'"
        :error="errors.company"
        @blur="validateField('company')"
      />
      <FormFieldInput
        v-model="title"
        name="title"
        :parent-name="name"
        property-name="title"
        label="signup.jobTitle"
        :icon="'briefcase'"
        :error="errors.title"
        @blur="validateField('title')"
      />
      <FormFieldInput
        v-model="country"
        name="country"
        :parent-name="name"
        property-name="country"
        type="select"
        label="signup.country"
        :icon="'globe'"
        :items="countries"
        :items-loading="countriesLoading || regionsLoading"
        required
        :error="errors.country"
        @blur="validateField('country')"
      />
      <Transition
        v-if="isCountryDividedToRegions"
        enter-active-class="transition-all duration-500 ease-out"
        leave-active-class="transition-all duration-500 ease-in"
        enter-from-class="translate-y-[-20px] opacity-0"
        leave-to-class="translate-y-[-20px] opacity-0"
        appear
      >
        <FormFieldInput
          v-model="region"
          name="region"
          :parent-name="name"
          property-name="region"
          type="select"
          label="signup.region"
          :icon="'flag'"
          :items="regions[country]"
          :items-loading="regionsLoading"
          required
          :error="errors.region"
          @blur="validateField('region')"
        />
      </Transition>
      <Transition
        v-if="isCountryDividedToPostCodes"
        enter-active-class="transition-all duration-500 ease-out"
        leave-active-class="transition-all duration-500 ease-in"
        enter-from-class="translate-y-[-20px] opacity-0"
        leave-to-class="translate-y-[-20px] opacity-0"
        appear
      >
        <FormFieldInput
          v-model="postCode"
          name="postCode"
          :parent-name="name"
          property-name="postCode"
          label="emails.postCode"
          :icon="'flag'"
          required
          :error="errors.postCode"
          @blur="validateField('postCode')"
        />
      </Transition>
    </div>
    <FormFieldInput
      v-model="message"
      type="textarea"
      align-start
      name="message"
      :parent-name="name"
      property-name="message"
      label="base.products.help.form.message"
      :icon="'message'"
      :error="errors.title"
    />

    <template v-if="!hideSubmit">
      <button
        class="mt-10 w-full bg-black px-8 py-4 text-xl font-bold text-white disabled:bg-neutral-400"
        type="submit"
        :disabled="!valid"
      >
        {{ $t('configurator.order.makeRequest') }}
      </button>
      <UiRecaptchaText class="pl-8" />
    </template>
  </form>
</template>
